import React, { useState } from 'react';
import { X, CheckCircle, XCircle, AlertCircle, Package, Copy, Plus, ChevronDown, ChevronUp, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { robotoRegularBase64, robotoBoldBase64 } from '../ecc/roboto';

const ImportReportModal = ({ isOpen, onClose, reportData }) => {
  const [expandedSections, setExpandedSections] = useState({
    types: true,
    models: true,
    imported: false,
    skipped: true
  });

  if (!isOpen || !reportData) return null;

  const { summary, details, message } = reportData;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExportToPDF = () => {
    const doc = new jsPDF();

    // Add custom fonts with Polish characters support
    doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldBase64);
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');

    // Set default font
    doc.setFont('Roboto', 'normal');

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Equipment Import Report', margin, yPos);
    yPos += 8;

    // Date
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, yPos);
    yPos += 10;

    // Summary message
    doc.setFontSize(11);
    doc.text(message, margin, yPos);
    yPos += 10;

    // Summary statistics
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', margin, yPos);
    yPos += 7;

    doc.autoTable({
      startY: yPos,
      head: [['Metric', 'Count']],
      body: [
        ['Total Rows', summary.total_rows],
        ['Imported Items', summary.imported_items],
        ['Skipped Items', summary.skipped_items],
        ['Types Created', summary.types_created],
        ['Models Created (New)', summary.models_created],
        ['Models Copied (From Global)', summary.models_copied_from_global],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235], font: 'Roboto', fontStyle: 'bold' },
      bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
      margin: { left: margin },
    });
    yPos = doc.lastAutoTable.finalY + 10;

    // Equipment Types
    if (details.types.created.length > 0 || details.types.existing.length > 0) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Equipment Types', margin, yPos);
      yPos += 7;

      if (details.types.created.length > 0) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Created (${details.types.created.length})`, margin, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Type Name', 'Source']],
          body: details.types.created.map(t => [t.name, t.source]),
          theme: 'plain',
          headStyles: { fillColor: [34, 197, 94], textColor: 255, font: 'Roboto', fontStyle: 'bold' },
          bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
          margin: { left: margin },
        });
        yPos = doc.lastAutoTable.finalY + 5;
      }

      if (details.types.existing.length > 0) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Already Existing (${details.types.existing.length})`, margin, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Type Name']],
          body: details.types.existing.map(t => [t.name]),
          theme: 'plain',
          headStyles: { fillColor: [156, 163, 175], textColor: 255, font: 'Roboto', fontStyle: 'bold' },
          bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
          margin: { left: margin },
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
    }

    // Equipment Models
    if (details.models.created_new.length > 0 || details.models.copied_from_global.length > 0 || details.models.existing.length > 0) {
      if (yPos > 240) { doc.addPage(); yPos = 20; }
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Equipment Models', margin, yPos);
      yPos += 7;

      if (details.models.created_new.length > 0) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Created New (${details.models.created_new.length})`, margin, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Brand', 'Model', 'Type']],
          body: details.models.created_new.map(m => [m.brand, m.name, m.type]),
          theme: 'plain',
          headStyles: { fillColor: [34, 197, 94], textColor: 255, font: 'Roboto', fontStyle: 'bold' },
          bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
          margin: { left: margin },
        });
        yPos = doc.lastAutoTable.finalY + 5;
      }

      if (details.models.copied_from_global.length > 0) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Copied from Global (${details.models.copied_from_global.length})`, margin, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Brand', 'Model', 'Type']],
          body: details.models.copied_from_global.map(m => [m.brand, m.name, m.type]),
          theme: 'plain',
          headStyles: { fillColor: [59, 130, 246], textColor: 255, font: 'Roboto', fontStyle: 'bold' },
          bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
          margin: { left: margin },
        });
        yPos = doc.lastAutoTable.finalY + 5;
      }

      if (details.models.existing.length > 0) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`Already Existing (${details.models.existing.length})`, margin, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Brand', 'Model', 'Type']],
          body: details.models.existing.map(m => [m.brand, m.name, m.type]),
          theme: 'plain',
          headStyles: { fillColor: [156, 163, 175], textColor: 255, font: 'Roboto', fontStyle: 'bold' },
          bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
          margin: { left: margin },
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
    }

    // Imported Items
    if (details.items.imported.length > 0) {
      if (yPos > 240) { doc.addPage(); yPos = 20; }
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Imported Items (${details.items.imported.length})`, margin, yPos);
      yPos += 7;

      doc.autoTable({
        startY: yPos,
        head: [['Row', 'Serial Number', 'Type', 'Brand', 'Model']],
        body: details.items.imported.map(item => [
          item.row,
          item.serial_number,
          item.type,
          item.brand,
          item.model
        ]),
        theme: 'striped',
        headStyles: { fillColor: [34, 197, 94], font: 'Roboto', fontStyle: 'bold' },
        bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
        margin: { left: margin },
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Skipped Items
    if (details.items.skipped.length > 0) {
      if (yPos > 240) { doc.addPage(); yPos = 20; }
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Skipped Items (${details.items.skipped.length})`, margin, yPos);
      yPos += 7;

      doc.autoTable({
        startY: yPos,
        head: [['Row', 'Reason', 'Type', 'Brand', 'Model', 'SN']],
        body: details.items.skipped.map(item => [
          item.row,
          item.reason,
          item.data.type,
          item.data.brand,
          item.data.model,
          item.data.serial_number
        ]),
        theme: 'striped',
        headStyles: { fillColor: [239, 68, 68], font: 'Roboto', fontStyle: 'bold' },
        bodyStyles: { font: 'Roboto', fontStyle: 'normal' },
        margin: { left: margin },
        styles: { fontSize: 8, font: 'Roboto' },
      });
    }

    // Save PDF
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    doc.save(`equipment-import-report-${timestamp}.pdf`);
  };

  const SectionHeader = ({ title, count, icon: Icon, color, section }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <div className="flex items-center space-x-2">
        <Icon className={`h-5 w-5 ${color}`} />
        <span className="font-semibold text-gray-700">{title}</span>
        <span className="text-sm text-gray-500">({count})</span>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="h-5 w-5 text-gray-400" />
      ) : (
        <ChevronDown className="h-5 w-5 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Import Report</h3>
                  <p className="text-blue-100 text-sm">{message}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-blue-100 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <div className="text-sm text-gray-500 mb-1">Total Rows</div>
                <div className="text-2xl font-bold text-gray-900">{summary.total_rows}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-200">
                <div className="text-sm text-green-600 mb-1">Imported</div>
                <div className="text-2xl font-bold text-green-700">{summary.imported_items}</div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg shadow-sm border border-red-200">
                <div className="text-sm text-red-600 mb-1">Skipped</div>
                <div className="text-2xl font-bold text-red-700">{summary.skipped_items}</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-200">
                <div className="text-sm text-blue-600 mb-1">Types Created</div>
                <div className="text-2xl font-bold text-blue-700">{summary.types_created}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg shadow-sm border border-purple-200">
                <div className="text-sm text-purple-600 mb-1">Models Created</div>
                <div className="text-2xl font-bold text-purple-700">{summary.models_created}</div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg shadow-sm border border-indigo-200">
                <div className="text-sm text-indigo-600 mb-1">Models Copied</div>
                <div className="text-2xl font-bold text-indigo-700">{summary.models_copied_from_global}</div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="px-6 py-4 max-h-[500px] overflow-y-auto">
            <div className="space-y-4">

              {/* Types Section */}
              {(details.types.created.length > 0 || details.types.existing.length > 0) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <SectionHeader
                    title="Equipment Types"
                    count={details.types.created.length + details.types.existing.length}
                    icon={Package}
                    color="text-blue-600"
                    section="types"
                  />
                  {expandedSections.types && (
                    <div className="p-4 space-y-3">
                      {details.types.created.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center">
                            <Plus className="h-4 w-4 mr-1" />
                            Created ({details.types.created.length})
                          </h4>
                          <div className="space-y-1">
                            {details.types.created.map((type, idx) => (
                              <div key={idx} className="text-sm bg-green-50 px-3 py-2 rounded border border-green-200">
                                <span className="font-medium">{type.name}</span>
                                <span className="text-green-600 ml-2 text-xs">(copied from global)</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {details.types.existing.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Already Existing ({details.types.existing.length})
                          </h4>
                          <div className="space-y-1">
                            {details.types.existing.map((type, idx) => (
                              <div key={idx} className="text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200">
                                {type.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Models Section */}
              {(details.models.created_new.length > 0 || details.models.copied_from_global.length > 0 || details.models.existing.length > 0) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <SectionHeader
                    title="Equipment Models"
                    count={details.models.created_new.length + details.models.copied_from_global.length + details.models.existing.length}
                    icon={Copy}
                    color="text-purple-600"
                    section="models"
                  />
                  {expandedSections.models && (
                    <div className="p-4 space-y-3">
                      {details.models.created_new.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center">
                            <Plus className="h-4 w-4 mr-1" />
                            Created New ({details.models.created_new.length})
                          </h4>
                          <div className="space-y-1">
                            {details.models.created_new.map((model, idx) => (
                              <div key={idx} className="text-sm bg-green-50 px-3 py-2 rounded border border-green-200">
                                <span className="font-medium">{model.brand} {model.name}</span>
                                <span className="text-gray-500 ml-2">({model.type})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {details.models.copied_from_global.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center">
                            <Copy className="h-4 w-4 mr-1" />
                            Copied from Global ({details.models.copied_from_global.length})
                          </h4>
                          <div className="space-y-1">
                            {details.models.copied_from_global.map((model, idx) => (
                              <div key={idx} className="text-sm bg-blue-50 px-3 py-2 rounded border border-blue-200">
                                <span className="font-medium">{model.brand} {model.name}</span>
                                <span className="text-gray-500 ml-2">({model.type})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {details.models.existing.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Already Existing ({details.models.existing.length})
                          </h4>
                          <div className="space-y-1">
                            {details.models.existing.map((model, idx) => (
                              <div key={idx} className="text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200">
                                <span className="font-medium">{model.brand} {model.name}</span>
                                <span className="text-gray-500 ml-2">({model.type})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Imported Items */}
              {details.items.imported.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <SectionHeader
                    title="Imported Items"
                    count={details.items.imported.length}
                    icon={CheckCircle}
                    color="text-green-600"
                    section="imported"
                  />
                  {expandedSections.imported && (
                    <div className="p-4">
                      <div className="max-h-60 overflow-y-auto space-y-1">
                        {details.items.imported.map((item, idx) => (
                          <div key={idx} className="text-sm bg-green-50 px-3 py-2 rounded border border-green-200 flex justify-between">
                            <span>
                              <span className="font-medium">SN: {item.serial_number}</span>
                              <span className="text-gray-500 ml-2">- {item.brand} {item.model} ({item.type})</span>
                            </span>
                            <span className="text-xs text-gray-400">Row {item.row}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Skipped Items */}
              {details.items.skipped.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <SectionHeader
                    title="Skipped Items"
                    count={details.items.skipped.length}
                    icon={XCircle}
                    color="text-red-600"
                    section="skipped"
                  />
                  {expandedSections.skipped && (
                    <div className="p-4">
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {details.items.skipped.map((item, idx) => (
                          <div key={idx} className="bg-red-50 px-3 py-3 rounded border border-red-200">
                            <div className="flex items-start justify-between mb-1">
                              <span className="text-sm font-semibold text-red-700">Row {item.row}</span>
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            </div>
                            <div className="text-sm text-red-600 mb-2">{item.reason}</div>
                            <div className="text-xs text-gray-600 bg-white px-2 py-1 rounded">
                              Type: <span className="font-medium">{item.data.type}</span> |
                              Brand: <span className="font-medium">{item.data.brand}</span> |
                              Model: <span className="font-medium">{item.data.model}</span>
                              {item.data.serial_number && (
                                <> | SN: <span className="font-medium">{item.data.serial_number}</span></>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <button
                onClick={handleExportToPDF}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>Save as PDF</span>
              </button>
              <button
                onClick={onClose}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportReportModal;
